import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImpactMetrics = ({ userImpact }) => {
  const navigate = useNavigate();

  const handleViewFullImpact = () => {
    navigate('/impact-tracking');
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-MX')?.format(num);
  };

  const impactCards = [
    {
      id: 'food-rescued',
      title: 'Comida Rescatada',
      value: userImpact?.foodRescued,
      unit: 'kg',
      icon: 'Package',
      color: 'text-success',
      bgColor: 'bg-success/10',
      equivalency: `${userImpact?.mealsEquivalent} comidas salvadas`
    },
    {
      id: 'water-saved',
      title: 'Agua Ahorrada',
      value: userImpact?.waterSaved,
      unit: 'L',
      icon: 'Droplets',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      equivalency: `${userImpact?.showerEquivalent} duchas de 10 min`
    },
    {
      id: 'co2-avoided',
      title: 'CO₂ Evitado',
      value: userImpact?.co2Avoided,
      unit: 'kg',
      icon: 'Leaf',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      equivalency: `${userImpact?.treesEquivalent} árboles plantados`
    },
    {
      id: 'money-saved',
      title: 'Dinero Ahorrado',
      value: userImpact?.moneySaved,
      unit: 'MXN',
      icon: 'DollarSign',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      equivalency: `${userImpact?.discountPercentage}% de descuento promedio`
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-foreground">
            Tu Impacto Ambiental
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Desde que comenzaste a rescatar comida
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewFullImpact}
          iconName="TrendingUp"
          iconPosition="left"
        >
          Ver detalles
        </Button>
      </div>
      {/* Impact Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {impactCards?.map((card) => (
          <div
            key={card?.id}
            className="p-4 rounded-lg border border-border hover:shadow-warm-sm transition-smooth"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className={`w-10 h-10 rounded-full ${card?.bgColor} flex items-center justify-center`}>
                <Icon name={card?.icon} size={20} className={card?.color} />
              </div>
              <div>
                <h3 className="font-body font-medium text-foreground text-sm">
                  {card?.title}
                </h3>
              </div>
            </div>
            
            <div className="mb-2">
              <div className="flex items-baseline space-x-1">
                <span className="text-2xl font-bold text-foreground">
                  {card?.unit === 'MXN' ? '$' : ''}{formatNumber(card?.value)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {card?.unit !== 'MXN' ? card?.unit : ''}
                </span>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {card?.equivalency}
            </p>
          </div>
        ))}
      </div>
      {/* Streak and Achievements */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Icon name="Flame" size={18} color="var(--color-accent)" />
              <span className="font-body font-medium text-foreground">
                Racha actual: {userImpact?.currentStreak} días
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={18} color="var(--color-warning)" />
              <span className="font-body font-medium text-foreground">
                Nivel: {userImpact?.level}
              </span>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-xs text-muted-foreground">
              Próximo nivel en
            </p>
            <p className="font-medium text-foreground text-sm">
              {userImpact?.pointsToNextLevel} puntos
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Progreso al siguiente nivel</span>
            <span>{userImpact?.levelProgress}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-smooth"
              style={{ width: `${userImpact?.levelProgress}%` }}
            />
          </div>
        </div>
      </div>
      {/* Recent Achievements */}
      {userImpact?.recentAchievements && userImpact?.recentAchievements?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="font-body font-medium text-foreground text-sm mb-3">
            Logros Recientes
          </h4>
          <div className="space-y-2">
            {userImpact?.recentAchievements?.map((achievement, index) => (
              <div key={index} className="flex items-center space-x-3 p-2 bg-muted rounded-md">
                <Icon name="Trophy" size={16} color="var(--color-warning)" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {achievement?.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {achievement?.description}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {achievement?.date}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactMetrics;